import type { TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import {
  MultiDatePicker,
  type MultiDatePickerProps,
} from '../multi-date-picker';

type RHFMultiDatePickerProps<TField extends FieldValues> = {
  label?: string;
  datePickerProps?: Omit<
    MultiDatePickerProps,
    'onChange' | 'value' | 'renderInput'
  >;
  controlProps: UseControllerProps<TField>;
  inputProps?: Omit<TextFieldProps, 'variant'> & {
    variant?: TextFieldProps['variant'] | 'white-filled';
  };
};

export const RHFMultiDatePicker = <TField extends FieldValues>({
  label,
  datePickerProps,
  controlProps,
  inputProps,
}: RHFMultiDatePickerProps<TField>) => {
  const {
    field: { ref, onBlur, name, value, onChange },
    fieldState: { error },
  } = useController(controlProps);
  const { spacing, palette } = useTheme();

  const { variant } = inputProps ?? {};
  const isWhiteFilledVariant = variant === 'white-filled';

  return (
    <MultiDatePicker
      {...datePickerProps}
      onChange={onChange}
      value={value}
      label={label}
      inputRef={ref}
      slotProps={{
        textField: {
          ...inputProps,
          variant: isWhiteFilledVariant ? 'filled' : variant,
          sx: {
            ...inputProps?.sx,
            ...(isWhiteFilledVariant && {
              '& .MuiInputBase-root, & .MuiInputBase-root.Mui-focused': {
                backgroundColor: palette.background.default,
                borderRadius: spacing(1),
              },
              '& .MuiInputBase-root:hover': {
                backgroundColor: palette.primary.lighter,
              },
            }),
          },
          onBlur,
          name,
          error: !!error,
          helperText: error?.message,
        },
      }}
    />
  );
};
